import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import GeneralForm from '../../components/generalForm';
import CustomSelect from '../../components/customFields/customSelect';
import ROUTE_STATUS from '../../components/constants/routeStatus';
import utils from '../../utils/utils';
import ErrorMessage from '../../components/errorMessage';
import MultiSelect from '../../components/customFields/multiSelect';
import GooglePlacesField from '../../components/customFields/googlePlacesField';
import { useDrivers } from '../drivers/apiHooks';
import { useRoute, useRouteEdit } from './apiHooks';

function RouteForm({ routeId, onSuccess, compact, deliveriesWithoutRoutes = [] }) {
  const errorMessagesRef = useRef();
  const { formatMessage } = useIntl();

  const [formRoute, setFormRoute] = useState({});

  const { drivers, isLoading: isDriversLoading, isError: isDriversError, error: driversError } = useDrivers();

  const {
    route,
    isLoading: routeGetLoading,
    isRouteGetError,
    routeGetError,
  } = useRoute({
    routeId,
    onSuccess: (route) => {
      setFormRoute({
        ...route,
        driverId: route.driver ? route.driver.id : null,
        deliveries: route.deliveries.map((delivery) => delivery.id),
        customStartDepot: !!route.startDepot,
      });
    },
  });

  const {
    editRoute,
    isLoading: routeEditLoading,
    isError: isRouteEditError,
    error: routeEditError,
  } = useRouteEdit((resultRoute) => onSuccess(resultRoute));

  useEffect(() => {
    if (routeEditError) {
      errorMessagesRef.current?.scrollIntoView({ block: 'nearest' });
    }
  }, [routeEditError]);

  const routeStatusOptions = Object.keys(ROUTE_STATUS).map((key) => {
    const type = ROUTE_STATUS[key].name;
    return {
      value: type,
      label: formatMessage({ id: `route.status.${type}` }),
    };
  });

  const driverOptions = drivers
    ? drivers.map((driver) => {
        return {
          value: driver.id,
          label: `#${driver.id} - ${driver.name}`,
        };
      })
    : [];

  const deliveryOptions =
    [...deliveriesWithoutRoutes, ...(route?.deliveries || [])].map((delivery) => ({
      value: delivery.id,
      label: `${delivery.name} #${delivery.id}`,
    })) || [];

  const formSchema = {
    type: 'object',
    required: [],
    properties: {
      status: {
        type: 'string',
        title: formatMessage({ id: 'route.form.field.status.label' }),
        options: routeStatusOptions,
      },
      driverId: {
        type: 'number',
        title: formatMessage({ id: 'route.form.field.driver.label' }),
        options: driverOptions,
        showClearOption: true,
      },
      deliveries: {
        type: 'array',
        title: formatMessage({ id: 'route.form.field.deliveries.label' }),
        options: deliveryOptions,
      },
      customStartDepot: {
        type: 'boolean',
        title: formatMessage({ id: 'route.form.field.custom.start.depot.label' }),
      },
    },
  };

  if (formRoute.customStartDepot) {
    formSchema.properties.startDepot = {
      title: formatMessage({ id: 'route.form.field.start.depot.label' }),
      compact,
      type: 'object',
      required: [],
      properties: {
        unit: {
          type: 'string',
          default: '',
        },
        street: {
          type: 'string',
          default: '',
        },
        city: {
          type: 'string',
          default: '',
        },
        country: {
          type: 'string',
          default: '',
        },
        postCode: {
          type: 'string',
          default: '',
        },
        coordinates: {
          type: 'object',
          required: [],
          properties: {
            lat: {
              type: 'string',
            },
            lon: {
              type: 'string',
            },
          },
        },
      },
    };
  }

  formSchema.properties.isFinal = {
    type: 'boolean',
    title: formatMessage({ id: 'route.form.field.is.final.label' }),
  };

  formSchema.properties.reoptimise = {
    type: 'boolean',
    title: formatMessage({ id: 'route.form.field.reoptimise.label' }),
  };

  const uiSchema = {
    startDepot: {
      'ui:field': 'googlePlacesField',
    },
    status: {
      'ui:field': 'customSelect',
    },
    driverId: {
      'ui:field': 'customSelect',
    },
    deliveries: {
      'ui:field': 'multiSelect',
    },
    customStartDepot: {
      'ui:help': formatMessage({ id: 'route.form.field.custom.start.depot.help' }),
    },
    isFinal: {
      'ui:help': formatMessage({ id: 'route.form.field.is.final.help' }),
    },
    reoptimise: {
      'ui:help': formatMessage({ id: 'route.form.field.reoptimise.help' }),
    },
  };

  const onFormSubmit = () => {
    editRoute({ route: formRoute });
  };

  const formFields = {
    customSelect: CustomSelect,
    multiSelect: MultiSelect,
    googlePlacesField: GooglePlacesField,
  };

  const buttonCopy = 'route.edit.form.submit';

  return (
    <div>
      {isDriversError ? <ErrorMessage error={utils.getErrorMessageFromResponse(driversError)} /> : ''}

      <GeneralForm
        title={formatMessage({ id: 'route.form.title' }, { routeId: route ? route.id : '-' })}
        onChange={(event) => setFormRoute(event.formData)}
        onSubmit={onFormSubmit}
        formSchema={formSchema}
        formUiSchema={uiSchema}
        formFields={formFields}
        formData={formRoute}
        buttonCopy={buttonCopy}
        errorMessage={
          isRouteEditError || isRouteGetError
            ? utils.getErrorMessageFromResponse(routeEditError || routeGetError, true)
            : null
        }
        loading={routeEditLoading || isDriversLoading || routeGetLoading}
        hideImageUpload={true}
        compact={compact}
        errorMessageRef={errorMessagesRef}
      />
    </div>
  );
}

RouteForm.propTypes = {
  onSuccess: PropTypes.func.isRequired,
};

RouteForm.defaultProps = {
  onSuccess: () => {},
};

export default RouteForm;
